import { useState, useEffect, useCallback } from "react";
import * as plm from "../services/plmService";

export function usePlm({ token, productId }) {
  const [products, setProducts] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [journeyMaps, setJourneyMaps] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [requirementTypes, setRequirementTypes] = useState([]);
  const [testCases, setTestCases] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ---------- Products ----------
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await plm.fetchProductsApi({ token });
      setProducts(data);
    } catch (err) {
      console.error(err);
      setError("Failed to load products.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  const createProduct = async (product) => {
    try {
      const created = await plm.createProductApi({ token, product });
      setProducts(prev => [created, ...prev]);
      return created;
    } catch (err) {
      console.error(err);
      setError("Failed to create product.");
    }
  };

  const updateProduct = async (id, product) => {
    try {
      const updated = await plm.updateProductApi({ token, productId: id, product });
      setProducts(prev => prev.map(p => p.id === id ? updated : p));
      return updated;
    } catch (err) {
      console.error(err);
      setError("Failed to update product.");
    }
  };

  const deleteProduct = async (id) => {
    try {
      await plm.deleteProductApi({ token, productId: id });
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error(err);
      setError("Failed to delete product.");
    }
  };

  // ---------- Personas ----------
  const fetchPersonas = useCallback(async () => {
    try {
      const data = await plm.fetchPersonasApi({ token, productId });
      setPersonas(data);
    } catch (err) {
      console.error(err);
      setError("Failed to load personas.");
    }
  }, [token, productId]);

  const createPersona = async (persona) => {
    try {
      const body = productId ? { ...persona, productId } : persona;
      const created = await plm.createPersonaApi({ token, persona: body });
      setPersonas(prev => [created, ...prev]);
      return created;
    } catch (err) {
      console.error(err);
      setError("Failed to create persona.");
    }
  };

  const updatePersona = async (personaId, persona) => {
    try {
      const updated = await plm.updatePersonaApi({ token, personaId, persona });
      setPersonas(prev => prev.map(p => p.id === personaId ? updated : p));
      return updated;
    } catch (err) {
      console.error(err);
      setError("Failed to update persona.");
    }
  };

  const deletePersona = async (personaId) => {
    try {
      await plm.deletePersonaApi({ token, personaId });
      setPersonas(prev => prev.filter(p => p.id !== personaId));
    } catch (err) {
      console.error(err);
      setError("Failed to delete persona.");
    }
  };

  // ---------- Journey Maps ----------
  const fetchJourneyMaps = useCallback(async () => {
    try {
      const data = await plm.fetchJourneyMapsApi({ token, productId });
      setJourneyMaps(data);
    } catch (err) {
      console.error(err);
      setError("Failed to load journey maps.");
    }
  }, [token, productId]);

  const createJourneyMap = async (journeyMap) => {
    try {
      const body = productId ? { ...journeyMap, productId } : journeyMap;
      const created = await plm.createJourneyMapApi({ token, journeyMap: body });
      setJourneyMaps(prev => [created, ...prev]);
      return created;
    } catch (err) {
      console.error(err);
      setError("Failed to create journey map.");
    }
  };

  const updateJourneyMap = async (journeyMapId, journeyMap) => {
    try {
      const updated = await plm.updateJourneyMapApi({ token, journeyMapId, journeyMap });
      setJourneyMaps(prev => prev.map(j => j.id === journeyMapId ? updated : j));
      return updated;
    } catch (err) {
      console.error(err);
      setError("Failed to update journey map.");
    }
  };

  const deleteJourneyMap = async (journeyMapId) => {
    try {
      await plm.deleteJourneyMapApi({ token, journeyMapId });
      setJourneyMaps(prev => prev.filter(j => j.id !== journeyMapId));
    } catch (err) {
      console.error(err);
      setError("Failed to delete journey map.");
    }
  };

  // ---------- Requirements ----------
  const fetchRequirements = useCallback(async () => {
    try {
      const data = await plm.fetchRequirementsApi({ token, productId });
      setRequirements(data);
    } catch (err) {
      console.error(err);
      setError("Failed to load requirements.");
    }
  }, [token, productId]);

  const fetchRequirementTypes = useCallback(async () => {
    try {
      const data = await plm.fetchRequirementTypesApi({ token });
      setRequirementTypes(data);
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  const createRequirement = async (requirement) => {
    try {
      const body = productId ? { ...requirement, productId } : requirement;
      const created = await plm.createRequirementApi({ token, requirement: body });
      setRequirements(prev => [created, ...prev]);
      return created;
    } catch (err) {
      console.error(err);
      setError("Failed to create requirement.");
    }
  };

  const updateRequirement = async (requirementId, requirement) => {
    try {
      const updated = await plm.updateRequirementApi({ token, requirementId, requirement });
      setRequirements(prev => prev.map(r => r.id === requirementId ? updated : r));
      return updated;
    } catch (err) {
      console.error(err);
      setError("Failed to update requirement.");
    }
  };

  const deleteRequirement = async (requirementId) => {
    try {
      await plm.deleteRequirementApi({ token, requirementId });
      setRequirements(prev => prev.filter(r => r.id !== requirementId));
    } catch (err) {
      console.error(err);
      setError("Failed to delete requirement.");
    }
  };

  // ---------- Test Cases ----------
  const fetchTestCases = useCallback(async () => {
    try {
      const data = await plm.fetchTestCasesApi({ token, productId });
      setTestCases(data);
    } catch (err) {
      console.error(err);
      setError("Failed to load test cases.");
    }
  }, [token, productId]);

  const createTestCase = async (testCase) => {
    try {
      const body = productId ? { ...testCase, productId } : testCase;
      const created = await plm.createTestCaseApi({ token, testCase: body });
      setTestCases(prev => [created, ...prev]);
      return created;
    } catch (err) {
      console.error(err);
      setError("Failed to create test case.");
    }
  };

  const updateTestCase = async (testCaseId, testCase) => {
    try {
      const updated = await plm.updateTestCaseApi({ token, testCaseId, testCase });
      setTestCases(prev => prev.map(t => t.id === testCaseId ? updated : t));
      return updated;
    } catch (err) {
      console.error(err);
      setError("Failed to update test case.");
    }
  };

  const deleteTestCase = async (testCaseId) => {
    try {
      await plm.deleteTestCaseApi({ token, testCaseId });
      setTestCases(prev => prev.filter(t => t.id !== testCaseId));
    } catch (err) {
      console.error(err);
      setError("Failed to delete test case.");
    }
  };

  // ---------- Tags ----------
  const fetchTags = useCallback(async () => {
    try {
      const data = await plm.fetchTagsApi({ token });
      setTags(data);
    } catch (err) {
      console.error(err);
    }
  }, [token]);

  // ---------- Initial Load ----------
  useEffect(() => {
    fetchProducts();
    fetchRequirementTypes();
    fetchTags();
  }, [fetchProducts, fetchRequirementTypes, fetchTags]);

  // Re-fetch product-scoped entities when productId changes
  useEffect(() => {
    fetchPersonas();
    fetchJourneyMaps();
    fetchRequirements();
    fetchTestCases();
  }, [fetchPersonas, fetchJourneyMaps, fetchRequirements, fetchTestCases]);

  return {
    products, personas, journeyMaps, requirements, requirementTypes, testCases, tags,
    loading, error,
    fetchProducts, createProduct, updateProduct, deleteProduct,
    fetchPersonas, createPersona, updatePersona, deletePersona,
    fetchJourneyMaps, createJourneyMap, updateJourneyMap, deleteJourneyMap,
    fetchRequirements, fetchRequirementTypes, createRequirement, updateRequirement, deleteRequirement,
    fetchTestCases, createTestCase, updateTestCase, deleteTestCase,
    fetchTags,
  };
}
